import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';

import { useMst } from '../../models/rootModel';

import { getColor } from './UICommon';
import { useAnalytics } from '../../utils/analytics';
import { sendGTMEvent } from '../../utils/googleTagManager';
import { ActionName } from '../../utils/mobileApp/mobileApp.types';
import { StepType } from '../../models/types/step.types';

import { ContainerBox } from '../styledComponent/Boxes.style';
import { MainTitleDescription } from './MainTitleDescription';

import arrowLeft from '../../assets/arrowLeftBlack.svg';
import { BaseStepButtons } from './BaseStepButtons';

const RootBox = styled(Box)`
  font-family: 'Poppins';
  line-height: 20px;
  font-size: 14px;
  color: ${getColor('BLACK_60')};
  font-weight: 400;
  min-height: calc(100vh - 88px);
  max-width: 528px;
  margin: 0 auto;
`;

const DivRootMobile = styled.div`
  display: none;
  
  position: sticky;
  position: -webkit-sticky;
  position: -moz-sticky;
  position: -o-sticky;
  position: -ms-sticky;
  bottom: 0;
  left: 0;
  background-color: ${getColor('DARK_CREAM')};
  border-top: 1px solid ${getColor('GRAY_20')};
  width: 100vw;
  @media (max-width: 600px) {
    display: block;
    width: 100%;
  }
`;

const Img = styled.img`
  padding-top: 24px;
  cursor: pointer;
`;

const ButtonsWrapper = styled.div`
  display: block;

  @media (max-width: 600px) {
    display: none;
  }
`;

export interface TitleAndDescriptionProps {
  title?: string;
  description?: string;
  failStepId?: string;
  marginBottom?: number;
  mainTitleImageUrl?: string;
  mainTitleImageBackgroundSize?: string;
  mainTitleImageHeight?: string;
  highlight?: string;
  isColoredDescription?: boolean;
  isTitleArrow?: boolean;
}

export interface StepProps {
  id: string;
  canGoBack?: boolean;
  stepType: StepType;
  ctaButtonTitle: string;
  goToNamedStep: (stepName: string) => void;
  type: string;
  stepIndex: number;
  // stepName = id
  stepName: string;
  isLoading: boolean;
}

export interface StepWizardProps {
  currentStep: number;
  firstStep: () => void;
  goToStep: (stepNumber: number) => void;
  lastStep: () => void;
  nextStep: () => void;
  previousStep: () => void;
  isActive: boolean;
  totalSteps: number;
}

export interface AnalyticsDataProps {
  analytics?: {};
}

export interface BaseStepProps extends TitleAndDescriptionProps, StepWizardProps, StepProps, AnalyticsDataProps {
  children?: React.ReactNode;
  changeButtonFunction?: () => void;
  isButtonDisabled?: boolean;
  showTwoMainFunnelButtons?: boolean;
  desktopChangeButtonFunction?: (isAndroid: boolean) => void;

  componentBelowCta?: React.ReactNode;
}

export const BaseStep = observer((props: BaseStepProps) => {
  const { UserProgress, UserDiscoveryResponse, Step } = useMst();

  const {
    nextStep,
    isActive,
    title,
    description,
    changeButtonFunction,
    goToNamedStep,
    canGoBack,
    stepType,
    mainTitleImageUrl,
    mainTitleImageBackgroundSize,
    mainTitleImageHeight,
    desktopChangeButtonFunction = () => {},
    isColoredDescription = false,
    highlight = '',
    isTitleArrow = false,
  } = props;

  const analytics = useAnalytics();
  const segmentObject = useRef({
    stepId: props.stepName,
    stepType: stepType,
    stepIndex: props.stepIndex,
    stepTitle: props.title,
    ...UserDiscoveryResponse.utms,
    origin: Step.origin,
    userID: UserDiscoveryResponse.accountId,
  });

  useEffect(() => {
    if (isActive) {
      sendGTMEvent({
        event: 'page_view',
        // todo: after we add routing this needs to update
        page_location: `${window.location.href}/${props.stepName}`,
        page_title: `Onboarding - ${title}`,
        user_id: UserDiscoveryResponse.accountId,
      });
    }
  }, [isActive, UserDiscoveryResponse.accountId, title, props.stepName]);

  useEffect(() => {
    if (isActive) {
      analytics.track(`view onboarding step`, { ...segmentObject.current, referringUrl: document.referrer });
    }
  }, [isActive, analytics, segmentObject, stepType]);

  useEffect(() => {
    if (UserDiscoveryResponse.accountId) {
      analytics.identify(UserDiscoveryResponse.accountId);
    }
  }, [UserDiscoveryResponse.accountId, analytics]);

  const onClick = () => {
    analytics.track(`click onboarding button`, {
      ...segmentObject.current,
      ...props?.analytics,
    });

    if (stepType !== StepType.ThinkingStep) {
      UserProgress.addStep(props.stepName);
    }

    if (changeButtonFunction) {
      changeButtonFunction();
      return;
    }

    nextStep();
  };

  const handlePreviousStep = () => {
    const lastStep = UserProgress.removeLastStep();

    if (stepType === StepType.Criteria || stepType === StepType.PickASegment) {
      UserDiscoveryResponse.removeUserAnswers(props.type);
    }

    if (stepType === StepType.PickAGroup) {
      UserDiscoveryResponse.removeGroupId();
    }

    if (!lastStep) {
      UserProgress.goToApp({ action: ActionName.Close });
      return;
    }

    goToNamedStep(lastStep);
  };

  return (
    <>
      {isActive && (
        <>
          <RootBox>
            <ContainerBox>
              {canGoBack ? <Img src={arrowLeft} onClick={handlePreviousStep} /> : null}
              <MainTitleDescription
                title={title}
                description={description}
                stepType={stepType}
                mainTitleImageUrl={mainTitleImageUrl}
                mainTitleImageBackgroundSize={mainTitleImageBackgroundSize}
                mainTitleImageHeight={mainTitleImageHeight}
                isColoredDescription={isColoredDescription}
                highlight={highlight}
                isTitleArrow={isTitleArrow}
              />
            </ContainerBox>

            {props.children}
            <ButtonsWrapper>
              <BaseStepButtons {...props} onClick={onClick} desktopChangeButtonFunction={desktopChangeButtonFunction} />
            </ButtonsWrapper>
            {props.componentBelowCta}
          </RootBox>
          <DivRootMobile>
            <BaseStepButtons {...props} onClick={onClick} desktopChangeButtonFunction={desktopChangeButtonFunction} />
          </DivRootMobile>
        </>
      )}
    </>
  );
});
